/**
 * ErrorBudgetGauge — SVG circular gauge showing SLO error budget consumption.
 */

interface ErrorBudgetGaugeProps {
  consumedPercent: number; // 0-100
  remainingMinutes: number;
  targetPercent: number;
  size?: number;
}

export function ErrorBudgetGauge({
  consumedPercent,
  remainingMinutes,
  targetPercent,
  size = 120,
}: ErrorBudgetGaugeProps) {
  const radius = (size - 16) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  // Clamp between 0 and 100
  const clamped = Math.min(100, Math.max(0, consumedPercent));
  const dashOffset = circumference * (1 - clamped / 100);

  const arcColor =
    clamped > 80 ? "var(--aptean-error, #e74c3c)" : "var(--aptean-accent, #54B3BE)";

  const remainingLabel =
    remainingMinutes >= 60
      ? `${Math.floor(remainingMinutes / 60)}h ${remainingMinutes % 60}m`
      : `${remainingMinutes}m`;

  return (
    <figure
      aria-label={`Error budget: ${clamped.toFixed(1)}% consumed, ${remainingLabel} remaining`}
      style={{ margin: 0, display: "inline-block" }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="aptean-error-budget-gauge"
        aria-hidden="true"
      >
        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="var(--aptean-surface-2, #2a2a3a)"
          strokeWidth={8}
        />
        {/* Consumed arc — starts at top (-90deg) */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={arcColor}
          strokeWidth={8}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: "stroke-dashoffset 0.4s ease" }}
        />
        {/* Center label */}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          dominantBaseline="auto"
          fontSize={size * 0.13}
          fill="var(--aptean-text-primary, #f0f0f5)"
          fontFamily="var(--aptean-font-body, sans-serif)"
        >
          {clamped.toFixed(1)}%
        </text>
        <text
          x={cx}
          y={cy + size * 0.12}
          textAnchor="middle"
          dominantBaseline="auto"
          fontSize={size * 0.1}
          fill="var(--aptean-text-secondary, #a0a0b8)"
          fontFamily="var(--aptean-font-body, sans-serif)"
        >
          consumed
        </text>
      </svg>
      <figcaption
        style={{
          textAlign: "center",
          fontSize: "0.75rem",
          color: "var(--aptean-text-secondary, #a0a0b8)",
          marginTop: 4,
        }}
      >
        {remainingLabel} remaining &bull; target {targetPercent}%
      </figcaption>
    </figure>
  );
}
